import { FeMap } from '@fe/maplibre-extensions';
import { MapMouseEvent } from 'maplibre-gl';
import { WeatherPickingInfoLayer } from '../../manager/types';
import { WeatherSettings, WeatherSettingsChangeSource } from '../../settings/settings';
import { Dispatcher } from '../../worker/dispatcher';
import { WeatherStoreProductInfo } from '../../worker/store/weather-store';
import { WeatherLayerBase } from '../weather-layer-base';

export class CompositeLayer extends WeatherLayerBase {
    constructor(
        protected readonly map: FeMap,
        workspaceId: number,
        dispatcher: Dispatcher,
        protected readonly productId: number,
        protected readonly weatherTopSlot: string,
        settings: WeatherSettings
    ) {
        super(map, workspaceId, dispatcher, productId, weatherTopSlot, settings);
    }

    private layers: WeatherLayerBase[] = [];

    protected async addLayer(layer: WeatherLayerBase) {
        this.layers.push(layer);
        if (this.attached) {
            layer.add();
        }

        layer.setVisibility(this.visible);
        if (this.info) {
            await layer.productUpdated(this.info);
        }
        layer.setVisibleFrame(this.visibleFrame);
    }

    protected removeLayer(layer: WeatherLayerBase) {
        const idx = this.layers.indexOf(layer);
        if (idx < 0) {
            return;
        }

        this.layers.splice(idx, 1);
        layer.remove();
    }

    async productUpdated(info: WeatherStoreProductInfo): Promise<void> {
        await super.productUpdated(info);
        await Promise.all(this.layers.map((x) => x.productUpdated(info)));
    }

    setVisibility(visible: boolean): void {
        super.setVisibility(visible);
        this.layers.forEach((x) => x.setVisibility(visible));
    }

    setVisibleFrame(frameId: number | null): Promise<void> {
        super.setVisibleFrame(frameId);
        this.layers.forEach((x) => x.setVisibleFrame(frameId));
        return Promise.resolve();
    }

    protected onAdd(): void {
        this.layers.forEach((x) => x.add());
    }

    protected onRemove(): void {
        this.layers.forEach((x) => x.remove());
    }

    shouldDisplay(): boolean {
        return super.shouldDisplay() && this.layers.some((x) => x.shouldDisplay());
    }

    async pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer[]> {
        const result = await Promise.all(this.layers.map((x) => x.pickObjects(event)));
        return result.map((x) => (Array.isArray(x) ? x : x ? [x] : [])).flat();
    }

    updateSettings(settings: WeatherSettings, source: WeatherSettingsChangeSource): void {
        super.updateSettings(settings, source);
        this.layers.forEach((x) => x.updateSettings(settings, source));
    }
}
